import { useCallback, useMemo, useState } from 'react';
import type { SearchMode } from '@/entities/search';

/** Initial search state for persistence (e.g. from useTableState). */
export type InitialSearchState = {
  query: string;
  mode: SearchMode;
};

export type SmartSearchState = {
  nlQuery: string;
  dslQuery: string;
  searchMode: SearchMode;
  setNlQuery: (query: string) => void;
  setDslQuery: (query: string) => void;
  setSearchMode: (mode: SearchMode) => void;
  clearSearch: () => void;
};

/** Search UI state (nl/dsl query, mode). Use with useSearchQuery or usePaginatedQuery for reactive UIs. */
export const useSmartSearchState = (params?: { initialSearch?: InitialSearchState }) => {
  const initial = params?.initialSearch;
  const [nlQuery, setNlQuery] = useState(initial ? (initial.mode === 'nl' ? initial.query : '') : '');
  const [dslQuery, setDslQuery] = useState(initial ? (initial.mode === 'dsl' ? initial.query : '') : '');
  const [searchMode, setSearchMode] = useState<SearchMode>(initial?.mode ?? 'nl');

  const clearSearch = useCallback(() => {
    setNlQuery('');
    setDslQuery('');
  }, []);

  const value = useMemo(() => ({ human: nlQuery, dsl: dslQuery }), [nlQuery, dslQuery]);

  const onChange = useCallback((val: { human: string; dsl: string }) => {
    setNlQuery(val.human);
    setDslQuery(val.dsl);
  }, []);

  return {
    value,
    onChange,
    setDslQuery,
    setNlQuery,
    searchMode,
    setSearchMode,
    clearSearch,
  };
};
